import React from 'react';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { PageHero } from '../components/PageHero';
import { Calendar, Info, ShieldCheck, BookOpen, Target, Phone, Mail, Facebook, CheckCircle2 } from 'lucide-react';
import { PlanYourVisitCTA } from '../components/PlanYourVisitCTA';
import { PHONE, PHONE_TEL, FACEBOOK_URL } from '../constants/business';
const LANCE_PHONE = '507-530-7854';
const LANCE_PHONE_TEL = 'tel:+15075307854';
const LANCE_EMAIL = 'lance@ksportsllc.com';
const LANCE_EMAIL_HREF = 'mailto:lance@ksportsllc.com';
export function PermitToCarry() {
  useDocumentMeta('Minnesota Permit to Carry Classes | Kjergaard Sports', 'Minnesota Permit to Carry classes at Kjergaard Sports — $100, no signup required, held on Saturdays in Lake Benton, Minnesota. Contact Lance for class updates.');
  const bringList = ['Handgun', 'Ammunition', 'Ear protection', 'Eye protection', 'Closed-toe shoes'];
  return <div data-id="element-1024">
      <PageHero title="Minnesota Permit to Carry Classes" image="https://cdn.magicpatterns.com/uploads/xfsXBN7fez9FhRqKJTeMqS/hero_image_for_a_Minnesota_Permit.png" alt="Indoor shooting range lane at Kjergaard Sports — ear protection, eye protection, handgun in case, ammunition, and silhouette target downrange" data-id="element-1025" />

      {/* Class details */}
      <section className="py-20 bg-brand-offwhite" data-id="element-1026">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1027">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1028">
            <Calendar className="w-12 h-12 text-brand-hunter mx-auto mb-6" data-id="element-1029" />
            <h2 className="text-3xl md:text-4xl font-serif text-brand-charcoal mb-6" data-id="element-1030">
              Class Details
            </h2>
            <p className="text-lg text-brand-charcoal/80 leading-relaxed" data-id="element-1031">
              Kjergaard Sports hosts Minnesota Permit to Carry classes covering
              safety fundamentals, legal responsibilities, and practical
              instruction. No signup is required — students can just show up on
              class day.
            </p>
          </div>

          {/* Quick facts grid */}
          <div className="grid md:grid-cols-2 gap-8 mb-16" data-id="element-1032">
            <div className="bg-brand-bone p-8 rounded-sm border border-brand-cream" data-id="element-1033">
              <h3 className="text-2xl font-serif text-brand-charcoal mb-6" data-id="element-1034">
                What to Expect
              </h3>
              <ul className="space-y-4 text-brand-charcoal/90" data-id="element-1035">
                <li className="flex items-start" data-id="element-1036">
                  <CheckCircle2 className="w-5 h-5 text-brand-hunter mr-3 flex-shrink-0 mt-0.5" data-id="element-1037" />
                  <span data-id="element-1038">
                    <strong data-id="element-1039">Class cost:</strong> $100
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1040">
                  <CheckCircle2 className="w-5 h-5 text-brand-hunter mr-3 flex-shrink-0 mt-0.5" data-id="element-1041" />
                  <span data-id="element-1042">
                    <strong data-id="element-1043">Signup:</strong> Not required — just show up
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1044">
                  <CheckCircle2 className="w-5 h-5 text-brand-hunter mr-3 flex-shrink-0 mt-0.5" data-id="element-1045" />
                  <span data-id="element-1046">
                    <strong data-id="element-1047">Day:</strong> Saturdays
                  </span>
                </li>
                <li className="flex items-start" data-id="element-1048">
                  <CheckCircle2 className="w-5 h-5 text-brand-hunter mr-3 flex-shrink-0 mt-0.5" data-id="element-1049" />
                  <span data-id="element-1050">
                    <strong data-id="element-1051">Schedule:</strong> Varies by demand — call or follow
                    Facebook for the next date
                  </span>
                </li>
              </ul>
            </div>

            <div className="bg-brand-bone p-8 rounded-sm border border-brand-cream" data-id="element-1052">
              <h3 className="text-2xl font-serif text-brand-charcoal mb-6" data-id="element-1053">
                What to Bring
              </h3>
              <ul className="space-y-4 text-brand-charcoal/90" data-id="element-1054">
                {bringList.map(item => <li key={item} className="flex items-start" data-id="element-1055">
                    <Target className="w-5 h-5 text-brand-leather mr-3 flex-shrink-0 mt-0.5" data-id="element-1056" />
                    <span data-id="element-1057">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>

          {/* Three pillars */}
          <div className="grid md:grid-cols-3 gap-8 mb-16" data-id="element-1058">
            <div className="bg-brand-bone p-8 rounded-sm border border-brand-cream text-center" data-id="element-1059">
              <ShieldCheck className="w-10 h-10 text-brand-leather mx-auto mb-4" data-id="element-1060" />
              <h3 className="text-xl font-serif text-brand-charcoal mb-3" data-id="element-1061">
                Safety First
              </h3>
              <p className="text-brand-charcoal/80 leading-relaxed" data-id="element-1062">
                Learn the core principles of safe firearm handling, storage, and
                situational awareness.
              </p>
            </div>
            <div className="bg-brand-bone p-8 rounded-sm border border-brand-cream text-center" data-id="element-1063">
              <BookOpen className="w-10 h-10 text-brand-leather mx-auto mb-4" data-id="element-1064" />
              <h3 className="text-xl font-serif text-brand-charcoal mb-3" data-id="element-1065">
                Legal Knowledge
              </h3>
              <p className="text-brand-charcoal/80 leading-relaxed" data-id="element-1066">
                Understand the laws and responsibilities associated with
                carrying a firearm in Minnesota.
              </p>
            </div>
            <div className="bg-brand-bone p-8 rounded-sm border border-brand-cream text-center" data-id="element-1067">
              <Target className="w-10 h-10 text-brand-leather mx-auto mb-4" data-id="element-1068" />
              <h3 className="text-xl font-serif text-brand-charcoal mb-3" data-id="element-1069">
                Practical Skills
              </h3>
              <p className="text-brand-charcoal/80 leading-relaxed" data-id="element-1070">
                Hands-on instruction to build confidence and proficiency with
                your handgun.
              </p>
            </div>
          </div>

          {/* Contact Lance */}
          <div className="max-w-4xl mx-auto bg-brand-hunter text-brand-offwhite p-10 rounded-sm mb-12" data-id="element-1071">
            <h3 className="text-2xl md:text-3xl font-serif mb-4" data-id="element-1072">
              Talk to Lance
            </h3>
            <p className="text-brand-bone/90 mb-8 leading-relaxed" data-id="element-1073">
              Lance is the main point of contact for Permit to Carry classes at
              Kjergaard Sports. Reach out for the next class date, questions
              about what to expect, or to confirm anything before showing up.
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-1074">
              <a href={LANCE_PHONE_TEL} className="inline-flex items-center justify-center px-6 py-3 bg-brand-leather hover:bg-brand-leather/90 text-brand-offwhite font-medium rounded-sm transition-colors" data-id="element-1075">
                <Phone className="w-5 h-5 mr-2" data-id="element-1076" />
                Call Lance: {LANCE_PHONE}
              </a>
              <a href={LANCE_EMAIL_HREF} className="inline-flex items-center justify-center px-6 py-3 border border-brand-cream/40 hover:bg-brand-cream/10 text-brand-offwhite font-medium rounded-sm transition-colors" data-id="element-1077">
                <Mail className="w-5 h-5 mr-2" data-id="element-1078" />
                Email Lance
              </a>
              <a href={FACEBOOK_URL} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center px-6 py-3 border border-brand-cream/40 hover:bg-brand-cream/10 text-brand-offwhite font-medium rounded-sm transition-colors" data-id="element-1079">
                <Facebook className="w-5 h-5 mr-2" data-id="element-1080" />
                Follow for Updates
              </a>
            </div>
            <p className="text-brand-bone/70 text-sm mt-6" data-id="element-1081">
              You can also call the store directly at{' '}
              <a href={PHONE_TEL} className="underline hover:text-brand-leather" data-id="element-1082">
                {PHONE}
              </a>
              .
            </p>
          </div>

          {/* Schedule note */}
          <div className="max-w-3xl mx-auto" data-id="element-1083">
            <div className="bg-brand-bone p-8 rounded-sm border border-brand-cream mb-8" data-id="element-1084">
              <div className="flex items-start" data-id="element-1085">
                <Info className="w-6 h-6 text-brand-leather mr-4 flex-shrink-0 mt-1" data-id="element-1086" />
                <div data-id="element-1087">
                  <h3 className="font-serif text-xl mb-2" data-id="element-1088">Schedule Updates</h3>
                  <p className="text-brand-charcoal/80 leading-relaxed" data-id="element-1089">
                    Class dates change based on demand. Call Lance, call the
                    store, or follow Kjergaard Sports on Facebook for the
                    current schedule and any last-minute updates.
                  </p>
                </div>
              </div>
            </div>

            {/* Legal disclaimer */}
            {/*
             IMPLEMENTATION NOTE (not customer-facing):
             Permit to Carry form submissions should route to lance@ksportsllc.com
             in final WebsiteForge setup.
             */}
            <div className="bg-brand-cream border-l-4 border-brand-leather p-6" data-id="element-1090">
              <p className="text-brand-charcoal/90 text-sm leading-relaxed" data-id="element-1091">
                <strong data-id="element-1092">Legal Disclaimer:</strong> Information on this page is
                general information only and is not legal advice. Requirements
                can vary. Obtaining a permit is subject to meeting all state and
                federal requirements. Kjergaard Sports does not guarantee permit
                approval. Please call the store with questions.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PlanYourVisitCTA data-id="element-1093" />
    </div>;
}